<template>
  <div>
    <Loading v-if="loading" />
    <div
      v-else
      class="hero-slider"
      :class="{ 'hero-slider--no-slider': !isSlider }">
      <!-- the big background slider (reuse some header image styles) -->
      <template v-if="slides.length">
        <div class="hero-slider-main swiper" ref="main">
          <div class="swiper-wrapper">
            <template v-for="slide in slides" :key="slide.id">
              <div
                class="swiper-slide"
                :class="[`swiper-slide--${slide.type.replace('_', '-')}`]">
                <div class="swiper-slide__content">
                  <div
                    class="hero-slider-main__image"
                    :class="[`hero-slider-main__image--${slide.type}`]">
                    <ResponsiveImage :images="slide.title_image" />
                  </div>
                  <div class="hero-slider-main__text-wrapper">
                    <div class="container">
                      <div class="hero-slider-main__text">
                        <span class="header-image__category">
                          {{ getEntityType(slide.type) }}
                        </span>
                        <h1
                          v-html="slide.title"
                          class="header-image__headline"></h1>
                        <div
                          v-if="slide.teaser !== ''"
                          class="header-image__description"
                          v-html="slide.teaser"></div>
                        <a :href="slide.alias" class="button">
                          {{ t.learnMore }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- thumbnails & navigation -->
        <div class="hero-slider-toolbar">
          <div class="container">
            <!-- thumbs slider on the bottom -->
            <div class="hero-slider-thumbs swiper" ref="thumbs">
              <div
                class="swiper-wrapper"
                :style="{ '--slides-count': slides.length }">
                <template v-for="(slide, idx) in slides" :key="slide.id">
                  <div
                    class="swiper-slide"
                    :class="{
                      'swiper-slide-thumb-active': idx === 0,
                      'swiper-slide--product-groups':
                        slide.type === 'product_groups',
                    }">
                    <div class="hero-slider-thumbs__content">
                      <div class="hero-slider-thumbs__image-wrapper">
                        <div class="hero-slider-thumbs__image">
                          <ResponsiveImage :images="slide.square_image" />
                        </div>
                      </div>
                      <div class="hero-slider-thumbs__text">
                        <span>{{ getEntityType(slide.type) }}</span>
                        <p v-html="slide.title"></p>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- navigation (reuse classes for styling) -->
            <div class="hero-slider__navigation">
              <div
                @click="navigationMove(-1)"
                class="navigation-button navigation-button--prev"></div>
              <div
                @click="navigationMove(1)"
                class="navigation-button navigation-button--next"></div>
            </div>
          </div>
        </div>
      </template>
      <template v-if="showError">
        <div class="hero-slider__error">
          <h2>{{ t.heroSlider.error }}</h2>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// 3rd
import Swiper, {
  Autoplay,
  Controller,
  EffectFade,
  Thumbs,
  Virtual,
} from 'swiper';
// components
import Loading from './elements/Loading';
import ResponsiveImage from './elements/ResponsiveImage';
// utils
import { getHeroSlides } from '../utils/api';
import translationStrings from '../translations/translationStrings';

export default {
  name: 'hero-slider',
  components: {
    Loading,
    ResponsiveImage,
  },
  props: {
    lang: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      showError: false,
      slides: [],
      swiper: null,
      t: translationStrings,
    };
  },
  computed: {
    isSlider() {
      return this.slides.length > 1;
    },
  },
  mounted() {
    this.fetchData();
  },
  updated() {
    // init after loading has stopped
    this.initSwiper();
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await getHeroSlides(this.lang);
        this.slides = data.data;
      } catch (error) {
        if (error.response) {
          this.showError = true;
          console.error(error.response);
        }
      } finally {
        this.loading = false;
      }
    },
    initSwiper() {
      const slidesCount = this.slides.length;
      const options = {
        slideToClickedSlide: true,
        observer: true,
        observeParents: true,
      };

      const galleryThumbs = new Swiper(this.$refs.thumbs, {
        ...options,
        breakpoints: {
          701: {
            slidesPerView: 2.5,
          },
          1181: {
            slidesPerView: slidesCount,
          },
        },
        slidesPerView: 1.5,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
      });

      this.swiper = new Swiper(this.$refs.main, {
        ...options,
        autoplay: {
          delay: 3000,
          waitForTransition: false,
        },
        allowTouchMove: false,
        controller: {
          control: galleryThumbs,
          by: 'container',
        },
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        modules: [Autoplay, Controller, EffectFade, Thumbs, Virtual],
        slidesPerView: 1,
        speed: 400,
        thumbs: {
          multipleActiveThumbs: false,
          swiper: galleryThumbs,
        },
        on: {
          autoplayStop() {
            if (!galleryThumbs) return;

            galleryThumbs.el.classList.add('hero-slider-thumbs--stop');
          },
        },
        virtualTranslate: true,
      });
    },
    navigationMove(direction) {
      // direction > 0 => move next
      // direction < 0 => move prev
      let whereTo =
        direction > 0
          ? this.swiper.activeIndex + 1
          : this.swiper.activeIndex - 1;

      if (this.swiper.isBeginning && direction < 0) {
        whereTo = this.slides.length;
      }

      if (this.swiper.isEnd && direction > 0) {
        whereTo = 0;
      }

      this.swiper.slideTo(whereTo);
    },
    getEntityType(entityType) {
      let type;

      switch (entityType) {
        case 'product_category':
          type = this.t.crops;
          break;
        case 'product_groups':
          type = this.t.productGroups;
          break;
        case 'article':
          type = this.t.featured;
          break;
        case 'news_press':
          type = this.t.news;
          break;
        default:
          break;
      }

      return type;
    },
  },
};
</script>
